import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CallLink, useCallRecord } from "@/components/calling/CallLink";
import { CallingToday } from "@/components/calling/CallingToday";
import { DailyBriefCard } from "@/components/ai/DailyBriefCard";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  CalendarCheck2,
  CheckCheck,
  Eye,
  FileSignature,
  Hourglass,
  Inbox,
  MessageCircle,
  Phone,
  RefreshCw,
  ShieldCheck,
  UserPlus,
  MapPin,
  TriangleAlert,
} from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ShellPanel } from "@/components/shell/ShellContext";
import { PanelHeading } from "@/components/shell/SectionPanel";
import { fmtDate, fmtMoney } from "@/components/reports/ReportWidgets";
import { WhatsAppChatDialog } from "@/components/whatsapp/WhatsAppChatDialog";
import { DealContextPanel } from "./DealContextPanel";
import { usePins } from "@/lib/usePins";
import { PanelNavLink } from "@/components/shell/SectionPanel";
import { useMyDay, type QueueFolder, type QueueGroup, type QueueItem, type QueueKind } from "./useMyDay";
import { dateLocale, withClock } from "@/lib/dateTime";

const KIND_STYLE: Record<QueueKind, { icon: React.ComponentType<{ className?: string }>; tint: string }> = {
  overdue: { icon: AlertCircle, tint: "bg-danger-soft text-danger" },
  task: { icon: CalendarCheck2, tint: "bg-muted text-muted-foreground" },
  view: { icon: Eye, tint: "bg-gold-soft text-gold-ink" },
  reply: { icon: MessageCircle, tint: "bg-success-soft text-success" },
  approve: { icon: ShieldCheck, tint: "bg-accent text-accent-foreground" },
  review: { icon: FileSignature, tint: "bg-accent text-accent-foreground" },
  waiting: { icon: Hourglass, tint: "bg-warning-soft text-warning" },
  lead: { icon: UserPlus, tint: "bg-info-soft text-info" },
  visit: { icon: MapPin, tint: "bg-success-soft text-success" },
  risk: { icon: TriangleAlert, tint: "bg-danger-soft text-danger" },
};

const GROUPS: { key: QueueGroup; label: string; tone: string }[] = [
  { key: "overdue", label: "Overdue", tone: "text-danger" },
  { key: "now", label: "Needs you now", tone: "text-muted-foreground" },
  { key: "later", label: "Later today", tone: "text-muted-foreground" },
];

const FOLDERS: { key: QueueFolder; label: string; hint: string }[] = [
  { key: "all", label: "Everything", hint: "Everything waiting for you" },
  { key: "followups", label: "Follow-ups", hint: "Calls, tasks, proposals they opened, new leads" },
  { key: "replies", label: "Replies", hint: "Unread WhatsApp messages" },
  { key: "approvals", label: "Approvals", hint: "Quotes to approve and proposals to review" },
  { key: "overdue", label: "Overdue", hint: "Tasks past their due date" },
  { key: "snoozed", label: "Snoozed", hint: "Put aside until later" },
];

/** When a snooze ends: in an hour, later today (5 pm), tomorrow 9 am, or next Monday 9 am */
const SNOOZE_CHOICES = (): { label: string; at: Date }[] => {
  const now = new Date();
  const at = (d: Date, h: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, 0, 0);
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (((8 - now.getDay()) % 7) || 7));
  const list = [{ label: "In 1 hour", at: new Date(now.getTime() + 3600000) }];
  if (now.getHours() < 16) list.push({ label: "Later today, 5 pm", at: at(now, 17) });
  list.push({ label: "Tomorrow, 9 am", at: at(tomorrow, 9) }, { label: "Next week, Monday 9 am", at: at(monday, 9) });
  return list;
};

function dealIdOf(item: QueueItem): string | null {
  if (item.risk) return item.risk.deal_id;
  if (item.proposal?.deal_id) return item.proposal.deal_id;
  if (item.quote?.deal?.id) return item.quote.deal.id;
  if (item.chat?.deal?.id) return item.chat.deal.id;
  if (item.task?.related_type === "DEAL" && item.task.related_id) return item.task.related_id;
  return null;
}

function recordLink(item: QueueItem): string | null {
  if (item.visit) return `${item.visit.related_type === "LEAD" ? "/leads" : "/accounts"}/${item.visit.related_id}`;
  const t = item.task;
  if (t?.related_id) {
    if (t.related_type === "DEAL") return `/deals/${t.related_id}`;
    if (t.related_type === "LEAD") return `/leads/${t.related_id}`;
    if (t.related_type === "ACCOUNT") return `/accounts/${t.related_id}`;
    if (t.related_type === "CONTACT") return `/contacts/${t.related_id}`;
    if (t.related_type === "QUOTE") return `/quotes/${t.related_id}`;
  }
  return null;
}

function QueueRow({ item, selected, onSelect }: { item: QueueItem; selected: boolean; onSelect: () => void }) {
  const style = KIND_STYLE[item.kind];
  const Icon = style.icon;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? "true" : undefined}
      className={cn(
        "flex w-full gap-3 border-b border-border px-4 py-2.5 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
        selected ? "bg-accent/60 shadow-[inset_0_0_0_1px_var(--primary)]" : "hover:bg-muted/50",
      )}
    >
      <span className={cn("mt-0.5 flex size-[30px] shrink-0 items-center justify-center rounded-lg", style.tint)}>
        <Icon className="size-[15px]" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[13px] font-semibold">{item.title}</span>
          <span className={cn("shrink-0 font-mono text-[11.5px]", item.overdue ? "text-danger" : "text-muted-foreground")}>{item.timeLabel}</span>
        </span>
        {item.account && <span className="truncate text-xs text-foreground/80">{item.account}</span>}
        {item.preview && <span className="truncate text-xs text-muted-foreground">{item.preview}</span>}
      </span>
    </button>
  );
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col rounded-lg bg-muted/60 px-3 py-2">
      <span className="text-[11px] font-medium text-muted-foreground">{label}</span>
      <span className="font-mono text-sm font-semibold">{value}</span>
    </div>
  );
}

function ActionLink({ to, href, children, primary }: { to?: string; href?: string; children: React.ReactNode; primary?: boolean }) {
  const cls = cn(
    "inline-flex h-9 items-center gap-1.5 rounded-lg px-3.5 text-[13px] font-semibold transition-colors",
    primary ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border border-border bg-card text-foreground hover:bg-muted",
  );
  if (href) {
    return (
      <a href={href} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to || "#"} className={cls}>
      {children}
    </Link>
  );
}

function Detail({
  item,
  onDone,
  busy,
  snoozedUntil,
  onSnooze,
  onUnsnooze,
  onWhatsApp,
}: {
  item: QueueItem;
  onDone: (item: QueueItem) => void;
  busy: boolean;
  snoozedUntil?: string;
  onSnooze: (item: QueueItem, at: Date) => void;
  onUnsnooze: (item: QueueItem) => void;
  onWhatsApp: (phone: string, name: string, dealId: string) => void;
}) {
  const dealId = dealIdOf(item);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  // "S" on the page opens this item's snooze menu
  useEffect(() => {
    const open = () => !snoozedUntil && setSnoozeOpen(true);
    window.addEventListener("myday-snooze", open);
    return () => window.removeEventListener("myday-snooze", open);
  }, [snoozedUntil, item.key]);
  const style = KIND_STYLE[item.kind];
  const Icon = style.icon;
  const link = recordLink(item);
  const phone = item.lead && "mobile" in item.lead ? item.lead.mobile || item.lead.phone : "";

  let facts: React.ReactNode = null;
  let actions: React.ReactNode = null;
  let heading = item.title;
  let sub = item.account;
  let body: React.ReactNode = item.preview ? <p className="text-sm text-foreground/90">{item.preview}</p> : null;

  if (item.proposal) {
    const p = item.proposal;
    sub = `${p.display_number} · ${p.account?.name || p.deal?.name || ""}`;
    facts = (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Fact label="Value" value={fmtMoney(Number(p.estimate_total))} />
        <Fact label="Opened" value={`${p.view_count}×`} />
        <Fact label="First opened" value={fmtDate(p.first_viewed_at)} />
        <Fact label="Valid until" value={fmtDate(p.valid_until)} />
      </div>
    );
    body =
      item.kind === "view" ? (
        <p className="text-sm text-foreground/90">The customer has opened this proposal. A quick call or WhatsApp now, while it is fresh, usually gets the decision moving.</p>
      ) : (
        <p className="text-sm text-foreground/90">Waiting for your review before it can be sent to the customer.</p>
      );
    actions = (
      <>
        <ActionLink to={`/proposals/${p.id}`} primary>
          {item.kind === "review" ? "Review proposal" : "Open proposal"}
        </ActionLink>
        {p.deal_id && <ActionLink to={`/deals/${p.deal_id}`}>Open deal</ActionLink>}
      </>
    );
  } else if (item.waiting) {
    const w = item.waiting;
    sub = `${w.display_number} · ${w.account_name || ""}`;
    body = <p className="text-sm text-foreground/90">Sent for approval to {w.approver_name || "a reviewer"}. It can't go to the customer until it is approved; if it waits too long it is reminded and then passed up.</p>;
    facts = (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Fact label="Value" value={fmtMoney(Number(w.grand_total))} />
        <Fact label="Sent" value={fmtDate(w.submitted_at)} />
      </div>
    );
    actions = (
      <ActionLink to={w.kind === "PROPOSAL" ? `/proposals/${w.id}` : `/quotes/${w.id}`} primary>
        Open {w.kind === "PROPOSAL" ? "proposal" : "quote"}
      </ActionLink>
    );
  } else if (item.quote) {
    const q = item.quote;
    sub = `${q.display_number} · ${q.account?.name || ""}`;
    facts = (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Fact label="Quote value" value={fmtMoney(Number(q.grand_total))} />
        <Fact label="Max discount" value={`${Number(q.max_discount_pct) || 0}%`} />
      </div>
    );
    actions = (
      <>
        <ActionLink to={`/quotes/${q.id}`} primary>
          Review &amp; decide
        </ActionLink>
        {q.deal?.id && <ActionLink to={`/deals/${q.deal.id}`}>Open deal</ActionLink>}
      </>
    );
  } else if (item.risk) {
    const r = item.risk;
    sub = "The AI's nightly deal check";
    body = (
      <div className="flex flex-col gap-2 text-sm text-foreground/90">
        {r.summary && <p>{r.summary}</p>}
        {r.next_action && (
          <p className="rounded-lg bg-muted/60 px-3 py-2">
            <span className="font-semibold">Next: </span>
            {r.next_action}
          </p>
        )}
      </div>
    );
    facts = (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Fact label="Deal value" value={fmtMoney(r.amount)} />
        <Fact label="Checked" value={fmtDate(r.assessed_at)} />
      </div>
    );
    actions = (
      <ActionLink to={`/deals/${r.deal_id}`} primary>
        Open deal
      </ActionLink>
    );
  } else if (item.visit) {
    const v = item.visit;
    body = <p className="text-sm text-foreground/90">{v.visit_status === "CHECKED_IN" ? "You're checked in here. Check out when you leave, with how it went." : `${v.purpose ? `${v.purpose}. ` : ""}Check in when you reach them; your phone's location is saved with the visit.`}</p>;
    actions = (
      <ActionLink to={`/visits?id=${v.id}`} primary>
        {v.visit_status === "CHECKED_IN" ? "Check out" : "Check in"}
      </ActionLink>
    );
  } else if (item.chat) {
    const c = item.chat;
    sub = c.profile_name ? `${c.profile_name} · ${c.phone}` : c.phone;
    body = (
      <blockquote className="rounded-lg border-l-2 border-success bg-muted/60 px-3 py-2 text-sm">{c.last_message_preview || "(media message)"}</blockquote>
    );
    facts = (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Fact label="Unread" value={c.unread_count} />
        <Fact label="Reply window" value={c.window_open ? "Open" : "Closed"} />
      </div>
    );
    actions = (
      <>
        <ActionLink to={`/whatsapp?c=${c.id}`} primary>
          Reply on WhatsApp
        </ActionLink>
        {c.deal?.id && <ActionLink to={`/deals/${c.deal.id}`}>Open deal</ActionLink>}
      </>
    );
  } else if (item.task) {
    const t = item.task;
    sub = [item.account, TASK_LABEL(t.task_type)].filter(Boolean).join(" · ");
    facts = (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Fact label="Due" value={new Date(t.due_at).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }))} />
        <Fact label="Priority" value={t.priority === "HIGH" ? "High" : t.priority === "LOW" ? "Low" : "Normal"} />
      </div>
    );
    body = t.description ? <p className="whitespace-pre-wrap text-sm text-foreground/90">{t.description}</p> : null;
    actions = (
      <>
        <Button size="sm" className="h-9 gap-1.5" disabled={busy} onClick={() => onDone(item)}>
          <CheckCheck className="size-4" />
          Mark done
        </Button>
        {link && <ActionLink to={link}>Open {RELATED_LABEL[t.related_type || ""] || "record"}</ActionLink>}
        <ActionLink to="/tasks">All tasks</ActionLink>
      </>
    );
  } else if (item.lead) {
    const l = item.lead;
    sub = l.company_name;
    facts =
      "score" in l ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Fact label="Score" value={l.score} />
          <Fact label="Status" value={String(l.lead_status || "").replace(/_/g, " ").toLowerCase()} />
        </div>
      ) : null;
    actions = (
      <>
        {phone && (
          <CallLink
            type="LEAD"
            id={l.id}
            number={phone}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary px-3.5 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <Phone className="size-4" />
            Call {phone}
          </CallLink>
        )}
        <ActionLink to={`/leads/${l.id}`} primary={!phone}>
          Open lead
        </ActionLink>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className={cn("mt-0.5 flex size-10 shrink-0 items-center justify-center rounded-xl", style.tint)}>
            <Icon className="size-5" />
          </span>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold leading-snug tracking-tight">{heading}</h2>
            {sub && <p className="text-sm text-muted-foreground">{sub}</p>}
            {snoozedUntil && <p className="mt-1 text-xs text-warning">Snoozed until {new Date(snoozedUntil).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }))}</p>}
          </div>
        </div>
        <div className="relative flex gap-1.5">
          {snoozedUntil ? (
            <Button variant="outline" size="sm" onClick={() => onUnsnooze(item)}>
              Bring back
            </Button>
          ) : (
            <Button variant="outline" size="sm" aria-expanded={snoozeOpen} onClick={() => setSnoozeOpen((o) => !o)}>
              Snooze
            </Button>
          )}
          {!snoozedUntil && (
            <Button variant="outline" size="sm" disabled={busy} onClick={() => onDone(item)}>
              Done
            </Button>
          )}
          {snoozeOpen && (
            <div role="menu" className="absolute right-0 top-9 z-20 flex w-52 flex-col rounded-lg border border-border bg-popover p-1 shadow-md">
              {SNOOZE_CHOICES().map((c) => (
                <button
                  key={c.label}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setSnoozeOpen(false);
                    onSnooze(item, c.at);
                  }}
                  className="rounded-md px-2.5 py-1.5 text-left text-[13px] hover:bg-muted"
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {facts}
      {body}
      {dealId && <DealContextPanel dealId={dealId} onWhatsApp={onWhatsApp} />}
      <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
  );
}

const RELATED_LABEL: Record<string, string> = { DEAL: "deal", LEAD: "lead", ACCOUNT: "account", CONTACT: "contact", QUOTE: "quote" };
const TASK_LABEL = (t: string) =>
  ({ CALL: "Call", EMAIL: "Email", MEETING: "Meeting", FOLLOW_UP: "Follow-up", WHATSAPP: "WhatsApp", OTHER: "Task" })[t] || "Task";

export function MyDayPage() {
  const { user } = useAuth();
  const { success, error } = useToast();
  const { items, snoozed, snoozes, counts, summary, numbers, loading, failed, reload, dismiss, snooze, unsnooze, markDone: persistDone } = useMyDay();
  // How the queue is ordered inside each group: most urgent first, or newest first
  const [sortBy, setSortBy] = useState<"priority" | "newest">("priority");
  const callRecord = useCallRecord();
  const [wa, setWa] = useState<{ phone: string; name: string; dealId: string } | null>(null);
  const { pins } = usePins();
  const [params, setParams] = useSearchParams();
  const folder = (params.get("folder") as QueueFolder) || "all";
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => (folder === "snoozed" ? snoozed : folder === "all" ? items : items.filter((i) => i.folder === folder)), [items, snoozed, folder]);
  const grouped = useMemo(
    () =>
      folder === "snoozed"
        ? [{ key: "snoozed" as QueueGroup, label: "Snoozed", tone: "text-muted-foreground", rows: [...filtered].sort((a, b) => new Date(snoozes[a.key] || 0).getTime() - new Date(snoozes[b.key] || 0).getTime()) }].filter((g) => g.rows.length > 0)
        : GROUPS.map((g) => ({ ...g, rows: filtered.filter((i) => i.group === g.key).sort((a, b) => (sortBy === "newest" ? b.at - a.at : a.at - b.at)) })).filter((g) => g.rows.length > 0),
    [filtered, folder, snoozes, sortBy],
  );
  const ordered = useMemo(() => grouped.flatMap((g) => g.rows), [grouped]);
  const selected = ordered.find((i) => i.key === selectedKey) || ordered[0] || null;

  const setFolder = (f: QueueFolder) => {
    const next = new URLSearchParams(params);
    if (f === "all") next.delete("folder");
    else next.set("folder", f);
    setParams(next, { replace: true });
    setSelectedKey(null);
  };

  const markItemDone = useCallback(
    async (item: QueueItem) => {
      setBusy(true);
      try {
        if (item.task) {
          await api.patch(`/api/Task/Complete/${item.task.id}`, { outcome: "Completed from My Day" });
          dismiss(item.key);
          success("Done", `"${item.title}" is marked as completed.`);
        } else {
          await persistDone(item.key);
          success("Done", `"${item.title}" is off your list.`);
        }
      } catch (e: any) {
        error("Could not complete", e?.message || "Try again");
      } finally {
        setBusy(false);
      }
    },
    [dismiss, persistDone, success, error],
  );

  const doSnooze = useCallback(
    async (item: QueueItem, at: Date) => {
      try {
        await snooze(item.key, at);
        success("Snoozed", `"${item.title}" comes back ${at.toLocaleString(dateLocale(), withClock({ weekday: "short", hour: "2-digit", minute: "2-digit" }))}.`);
      } catch (e: any) {
        error("Could not snooze", e?.message || "Try again");
      }
    },
    [snooze, success, error],
  );
  const doUnsnooze = useCallback(
    async (item: QueueItem) => {
      try {
        await unsnooze(item.key);
      } catch (e: any) {
        error("Could not bring it back", e?.message || "Try again");
      }
    },
    [unsnooze, error],
  );

  // J / K to move, E to mark the selected task done
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest("input, textarea, select, [contenteditable], [role=dialog]") || e.ctrlKey || e.metaKey || e.altKey) return;
      const idx = selected ? ordered.findIndex((i) => i.key === selected.key) : -1;
      if (e.key === "j" && idx < ordered.length - 1) setSelectedKey(ordered[idx + 1]?.key ?? null);
      else if (e.key === "k" && idx > 0) setSelectedKey(ordered[idx - 1]?.key ?? null);
      else if (e.key === "e" && selected) markItemDone(selected);
      else if (e.key === "s" && selected) window.dispatchEvent(new Event("myday-snooze"));
      else if (e.key === "c" && selected) {
        const phone = selected.lead && "mobile" in selected.lead ? selected.lead.mobile || selected.lead.phone : selected.chat?.phone;
        if (phone && selected.lead && "mobile" in selected.lead) void callRecord("LEAD", selected.lead.id, phone);
        else if (phone) error("Open the lead to call", "Calls are made from the CRM, on the page of the lead or contact.");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ordered, selected, markItemDone, callRecord, error]);

  const today = new Date().toLocaleDateString(dateLocale(), { weekday: "short", day: "numeric", month: "short" });
  const k = summary?.kpis;
  const first = user?.FirstName || "there";

  return (
    <div className="-m-4 flex min-h-full flex-col md:-m-6">
      <ShellPanel title="My Day" subtitle={`${today} · ${counts.all} to clear`}>
        <div className="flex flex-col gap-0.5">
          {FOLDERS.map((f) => {
            const active = folder === f.key;
            const n = counts[f.key];
            return (
              <button
                key={f.key}
                type="button"
                title={f.hint}
                onClick={() => setFolder(f.key)}
                aria-current={active ? "true" : undefined}
                className={cn(
                  "flex h-8 items-center justify-between rounded-md px-2.5 text-[13px] transition-colors",
                  active ? "bg-card font-semibold text-foreground shadow-[0_0_0_1px_var(--border)]" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                <span>{f.label}</span>
                <span className={cn("font-mono text-[11.5px]", f.key === "overdue" && n > 0 ? "text-danger" : active ? "text-primary" : "")}>{n}</span>
              </button>
            );
          })}
        </div>

        {String(user?.UserRole?.RoleCode || "").toUpperCase() !== "SALES_EXEC" && (
          <Link to="/dashboard" className="mt-3 px-2.5 text-xs font-medium text-primary hover:underline">
            ← Back to the team overview
          </Link>
        )}

        {k && (
          <div className="mt-2 flex flex-col gap-2 px-2.5">
            <PanelHeading>My numbers · {new Date().toLocaleDateString(dateLocale(), { month: "long" })}</PanelHeading>
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-muted-foreground">Won</span>
              <span className="font-mono text-sm font-semibold text-gold-ink">{fmtMoney(k.revenue)}</span>
            </div>
            {k.target_amount > 0 && (
              <>
                <div className="h-1.5 overflow-hidden rounded-full bg-gold-soft" role="progressbar" aria-valuenow={Math.round(k.target_achieved_pct || 0)} aria-valuemin={0} aria-valuemax={100}>
                  <div className="h-full rounded-full bg-gold" style={{ width: `${Math.min(100, k.target_achieved_pct || 0)}%` }} />
                </div>
                <div className="flex justify-between text-[11.5px] text-muted-foreground">
                  <span>{Math.round(k.target_achieved_pct || 0)}% of {fmtMoney(k.target_amount)}</span>
                  {numbers && <span>{numbers.days_left} days left</span>}
                </div>
              </>
            )}
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-muted-foreground">My pipeline</span>
              <span className="font-mono text-sm font-semibold">{fmtMoney(k.pipeline_amount)}</span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-muted-foreground">Open deals</span>
              <span className="font-mono text-sm font-semibold">{k.open_deals}</span>
            </div>
            {numbers && (
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-muted-foreground">Calls this week</span>
                <span className="font-mono text-sm font-semibold">{numbers.calls_this_week}</span>
              </div>
            )}
          </div>
        )}

        {pins.length > 0 && (
          <div className="mt-2 flex flex-col gap-0.5">
            <PanelHeading>Pinned</PanelHeading>
            {pins.map((p) => (
              <PanelNavLink key={p.id} to={p.to}>
                {p.label}
              </PanelNavLink>
            ))}
          </div>
        )}

        <div className="mt-4 px-2.5 text-[11.5px] leading-relaxed text-muted-foreground">
          <kbd className="rounded border border-border bg-card px-1 font-mono">J</kbd> <kbd className="rounded border border-border bg-card px-1 font-mono">K</kbd> move ·{" "}
          <kbd className="rounded border border-border bg-card px-1 font-mono">E</kbd> done ·{" "}
          <kbd className="rounded border border-border bg-card px-1 font-mono">S</kbd> snooze ·{" "}
          <kbd className="rounded border border-border bg-card px-1 font-mono">C</kbd> call
        </div>
      </ShellPanel>

      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 md:px-6">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Good {new Date().getHours() < 12 ? "morning" : new Date().getHours() < 17 ? "afternoon" : "evening"}, {first}</h1>
          <p className="text-sm text-muted-foreground">
            {loading ? "Checking what needs you…" : counts.all === 0 ? "You are all caught up." : `${counts.all} ${counts.all === 1 ? "thing needs" : "things need"} you today.`}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={reload} disabled={loading} className="gap-1.5">
          <RefreshCw className={cn("size-3.5", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      <DailyBriefCard />
      <CallingToday />

      {loading && items.length === 0 ? (
        <div className="space-y-3 p-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : failed ? (
        <div className="m-6 rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Could not load your day. Check your connection and press Refresh.
        </div>
      ) : ordered.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-10 text-center">
          <span className="flex size-12 items-center justify-center rounded-full bg-success-soft text-success">
            <Inbox className="size-6" />
          </span>
          <h2 className="text-base font-semibold">{folder === "all" ? "All clear" : "Nothing here"}</h2>
          <p className="max-w-sm text-sm text-muted-foreground">
            {folder === "all" ? "Nothing is waiting for you right now. New replies, proposal views and tasks will show up here." : "Nothing in this folder right now."}
          </p>
          {folder !== "all" && (
            <Button variant="outline" size="sm" onClick={() => setFolder("all")}>
              Show everything
            </Button>
          )}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col @4xl/main:flex-row">
          <section aria-label="Your list" className="w-full shrink-0 border-b border-border lg:w-[380px] lg:overflow-y-auto lg:border-b-0 lg:border-r">
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
              <span className="text-[13px] font-semibold">
                {FOLDERS.find((f) => f.key === folder)?.label || "Everything"} <span className="font-normal text-muted-foreground">· {ordered.length}</span>
              </span>
              <button type="button" onClick={() => setSortBy((s) => (s === "priority" ? "newest" : "priority"))} className="text-xs text-muted-foreground hover:text-foreground" aria-label="Change the order of the list">
                {sortBy === "priority" ? "Priority ▾" : "Newest ▾"}
              </button>
            </div>
            {grouped.map((g) => (
              <div key={g.key}>
                <div className={cn("border-b border-border bg-muted/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider", g.tone)}>
                  {g.label} <span className="font-mono font-normal">· {g.rows.length}</span>
                </div>
                {g.rows.map((item) => (
                  <QueueRow key={item.key} item={item} selected={selected?.key === item.key} onSelect={() => setSelectedKey(item.key)} />
                ))}
              </div>
            ))}
          </section>

          <section aria-label="Selected item" className="min-w-0 flex-1 overflow-y-auto p-5 md:p-7">
            {selected && (
              <Detail
                item={selected}
                onDone={markItemDone}
                busy={busy}
                snoozedUntil={folder === "snoozed" ? snoozes[selected.key] : undefined}
                onSnooze={doSnooze}
                onUnsnooze={doUnsnooze}
                onWhatsApp={(phone, name, dealId) => setWa({ phone, name, dealId })}
              />
            )}
            {wa && <WhatsAppChatDialog open onOpenChange={(o) => !o && setWa(null)} phone={wa.phone} name={wa.name} dealId={wa.dealId} />}
          </section>
        </div>
      )}
    </div>
  );
}
